import classNames from 'classnames'
import React, { MouseEventHandler, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PushPin } from '@mui/icons-material'
import { Button, ButtonProps } from '@mui/material'

import { Game } from '../../features/game/types/Game'
import CollectionMenu from '../../features/implementation-examples/components/CollectionMenu/CollectionMenu'
import { GRTooltip } from '../GRTooltip/GRTooltip'

export interface AddToCollectionButtonProps extends ButtonProps {
  iconOnly?: boolean
  screenshotId?: string
  game: Game
  /**
   * When provided, the button is rendered disabled and the tooltip is shown on hover
   * to explain why the action is not available (e.g. a screenshot without any related
   * feature would be invisible in the resulting collection, which only renders feature
   * groups — see `screenshotHasRelatedClassifier`).
   */
  disabledTooltip?: React.ReactNode
}

const AddToCollectionButton: React.FC<AddToCollectionButtonProps> = (props) => {
  const { t } = useTranslation()
  const [collectionAnchor, setCollectionAnchor] = useState<Element>()

  const onOpenCollectionSelector: MouseEventHandler = (evt) => {
    evt.stopPropagation()
    setCollectionAnchor(evt.currentTarget)
  }

  const onClose = () => {
    setCollectionAnchor(undefined)
  }

  if (!props.screenshotId) {
    return null
  }

  const isDisabled = !!props.disabledTooltip
  const button = (
    <Button
      className={classNames('AddToCollectionButton-root', props.className, { iconOnly: props.iconOnly })}
      color={props.color || 'primary'}
      onClick={onOpenCollectionSelector}
      startIcon={props.startIcon ?? <PushPin />}
      variant={props.variant || 'contained'}
      disabled={isDisabled}
    >
      {!props.iconOnly && (props.children || ' ' + t('implementations:label_add_to_collection'))}
    </Button>
  )

  return (
    <>
      {isDisabled ? (
        <GRTooltip content={props.disabledTooltip}>
          {/* MUI disables pointer events on disabled buttons; wrapper span lets the tooltip still receive hover. */}
          <span>{button}</span>
        </GRTooltip>
      ) : (
        button
      )}
      {!isDisabled && collectionAnchor && <CollectionMenu anchorEl={collectionAnchor} onClose={onClose} screenshotId={props.screenshotId} game={props.game} />}
    </>
  )
}

export default AddToCollectionButton
